import * as React from 'react';
import { cn } from '../../../utils/cn';

export interface StatTileProps {
  /**
   * Uppercase label rendered above the value (an icon optional).
   */
  label: React.ReactNode;
  /**
   * Big numeric/text value. Pass a node for compound layouts.
   */
  value: React.ReactNode;
  /**
   * Optional supplemental row under the value (delta or status).
   */
  delta?: React.ReactNode;
  /**
   * Sparkline / heartbeat slot, absolutely positioned bottom-right.
   */
  trend?: React.ReactNode;
  /**
   * Hover/focus link target — when present the tile becomes a link.
   */
  href?: string;
  /**
   * Extra classes for the outer card.
   */
  className?: string;
}

export function StatTile({ label, value, delta, trend, href, className }: StatTileProps) {
  const classes = {
    container: cn(
      'border-border bg-surface-1 relative flex flex-col gap-2 rounded-lg border px-4 py-3.5 transition-colors',
      href ? 'hover:border-hairline-strong' : null,
      className,
    ),
    label:
      'flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.08em] text-fg-3 [&>svg]:size-3',
    value: 'font-mono text-[24px] font-medium leading-none tabular-nums tracking-[-0.02em]',
    delta: 'font-mono text-[11px] tabular-nums text-fg-2',
    trend: 'pointer-events-none absolute bottom-2.5 right-3 opacity-55',
  };

  const inner = (
    <>
      <div className={classes.label}>{label}</div>
      <div className={classes.value}>{value}</div>
      {delta ? <div className={classes.delta}>{delta}</div> : null}
      {trend ? <div className={classes.trend}>{trend}</div> : null}
    </>
  );

  if (href)
    return (
      <a
        href={href}
        className={classes.container}
      >
        {inner}
      </a>
    );
  return <div className={classes.container}>{inner}</div>;
}
